<template>
  <MdsInput
    ref="mdsInput"
    :value="stringValue"
    :class="rootClass"
    :mwc-id="mwcId"
    :size="size"
    :label="label"
    :hidden-label="hiddenLabel"
    :placeholder="placeholder"
    :disabled="disabled"
    :error="error"
    :error-text="errorText"
    :required="required"
    :required-text="requiredText"
    :microcopy-above="microcopyAbove"
    :microcopy-below="microcopyBelow"
    @blur="onBlur"
    @keydown="processRawInput"
  />
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import MdsInput from '@mds/input';
import Cleave from 'cleave.js';
import parseDate from 'date-fns/parse';
import isValid from 'date-fns/isValid';
import formatDate from 'date-fns/format';
import FORMATS_CONFIG from './constants';
import defaultConfig from './_config';

const SIZES = ['small', 'medium', 'large'];

export const stringValueToDate = (stringValue, dateFormatFn) => {
  if (stringValue === '') {
    return null;
  }
  const parsedDate = parseDate(stringValue, dateFormatFn, new Date());
  if (isValid(parsedDate)) {
    return parsedDate;
  }
  throw new Error();
};

export default {
  name: 'hypo-date-input',
  components: { MdsInput },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    rootClass: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'medium',
      validator(value) {
        return SIZES.includes(value);
      },
    },
    error: Boolean,
    customErrorText: {
      type: String,
      default: '',
    },
    required: Boolean,
    requiredText: {
      type: String,
      default: '',
    },
    disabled: Boolean,
    dateFormat: {
      type: String,
      default: 'mmddyyyy_slash',
      validator(value) {
        return Object.keys(FORMATS_CONFIG).includes(value);
      },
    },
    microcopyAbove: {
      type: String,
      default: '',
    },
    microcopyBelow: {
      type: String,
      default: '',
    },
    value: {
      type: Date,
      default: null,
    },
    hiddenLabel: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      stringValue: '',
      cleaveInstance: null,
      defaultConfig,
    };
  },
  computed: {
    placeholder() {
      return FORMATS_CONFIG[this.dateFormat].placeholder;
    },
    dateFormatFn() {
      return FORMATS_CONFIG[this.dateFormat].dateFns;
    },
    errorText() {
      return [this.customErrorText];
    },
  },
  watch: {
    dateFormat() {
      this.createCleaveInstance();
    },
    value() {
      this.updateStringValue();
    },
  },
  mounted() {
    this.createCleaveInstance();
  },
  beforeDestroy() {
    this.destroyCleaveInstance();
  },
  methods: {
    destroyCleaveInstance() {
      if (this.cleaveInstance) {
        this.cleaveInstance.destroy();
      }
    },
    createCleaveInstance() {
      this.destroyCleaveInstance();
      const { $refs } = this;
      const { mdsInput } = $refs;
      const inputEl = mdsInput.$el.querySelector('input');
      // format string value before changing order with date-format applied
      this.updateStringValue();
      this.cleaveInstance = new Cleave(inputEl, {
        ...FORMATS_CONFIG[this.dateFormat].cleave,
        // updates the value properly
        onValueChanged: /* istanbul ignore next */ evt => {
          this.stringValue = evt.target.value;
          this.onInput();
        },
      });
    },
    updateStringValue() {
      // prevents updating the string value when there is an error
      if (!this.error && isValid(this.value)) {
        this.stringValue = formatDate(this.value, this.dateFormatFn);
      }
    },
    onBlur() {
      this.onInput('dateBlur');
    },
    onInput(eventName = 'input') {
      const { stringValue, dateFormatFn } = this;
      try {
        const dateValue = stringValueToDate(stringValue, dateFormatFn);
        this.$emit(eventName, dateValue, stringValue);
      } catch (err) {
        this.emitInvalidDate(eventName, stringValue);
      }
    },
    emitInvalidDate(eventName = 'input', stringValue = '') {
      this.$emit(eventName, null, stringValue); // invalid dates are considered null
      this.$emit('invalid-date');
    },
    processRawInput(event) {
      const { delimiter, dateFormatter } = this.cleaveInstance.properties;
      if (event.key !== delimiter) {
        return;
      }
      const { blocks, datePattern } = dateFormatter;
      let value = event.target.value.replace(/[^\d]/g, '');
      let result = '';
      blocks.forEach((length, index) => {
        if (value.length > 0) {
          let sub = value.slice(0, length);
          const rest = value.slice(length);
          const currentPattern = datePattern[index];
          if (currentPattern === 'd' || currentPattern === 'm') {
            const digitNum = parseInt(sub, 10);
            if (sub.length === 1 && digitNum === 0) {
              sub = '01';
            } else if (digitNum > 0 && digitNum < 10) {
              sub = `0${digitNum}`;
            }
          }
          result += sub;
          value = rest;
        }
      });
      this.cleaveInstance.setRawValue(result);
    },
  },
};
</script>

<style lang="scss">
@import '~@mds/constants';
@import './styles/variables';

.#{$component-ns} {
  background-color: $mds-background-color-white;
  text-align: center;
}
</style>
